import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function AdminLoginPage() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Check if user is actually an admin
      if (data.user.role !== 'admin') {
        throw new Error('Access denied. You do not have admin privileges.');
      }

      // Store credentials
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      navigate('/admin/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#0a1a0f' }}>
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-5/12 flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/60 to-slate-900/80"></div>
        <div className="relative">
          <Link to="/" className="flex items-center gap-2 mb-16">
            <div className="w-9 h-9 flex items-center justify-center bg-emerald-600 rounded-lg">
              <i className="ri-computer-line text-white text-lg"></i>
            </div>
            <span className="font-bold text-lg text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
              GCSE <span className="text-emerald-400">Computing</span>
            </span>
          </Link>
          <div className="mb-8">
            <div className="w-14 h-14 flex items-center justify-center bg-emerald-700/30 rounded-2xl mb-6">
              <i className="ri-shield-keyhole-line text-emerald-400 text-3xl"></i>
            </div>
            <h1 className="text-3xl font-extrabold text-white mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
              Admin Control Panel
            </h1>
            <p className="text-slate-400 text-sm leading-relaxed">
              Manage students, tutors, inquiries, events and all platform content from one place.
            </p>
          </div>
          <div className="flex flex-col gap-4">
            {[
              { icon: 'ri-mail-line', text: 'Manage student & tutor inquiries' },
              { icon: 'ri-user-star-line', text: 'Assign and manage tutors' },
              { icon: 'ri-calendar-event-line', text: 'Schedule and oversee events' },
              { icon: 'ri-bar-chart-2-line', text: 'View platform analytics' },
              { icon: 'ri-folder-open-line', text: 'Upload and manage resources' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3">
                <div className="w-7 h-7 flex items-center justify-center bg-emerald-700/30 rounded-lg flex-shrink-0">
                  <i className={`${item.icon} text-emerald-400 text-sm`}></i>
                </div>
                <p className="text-slate-300 text-sm">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-slate-600 text-xs">
          &copy; 2026 GCSE Computing · Admin Access Only
        </p>
      </div>

      {/* Right login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <div className="w-8 h-8 flex items-center justify-center bg-emerald-600 rounded-lg">
              <i className="ri-computer-line text-white text-base"></i>
            </div>
            <span className="font-bold text-lg text-white text-slate-900" style={{ fontFamily: "'Inter', sans-serif" }}>
              GCSE <span className="text-emerald-700">Computing</span>
            </span>
          </div>

          <div className="mb-8">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full mb-4">
              <i className="ri-shield-line text-xs"></i> Admin Access
            </span>
            <h2 className="text-2xl font-bold text-slate-800 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
              Sign in to Admin Panel
            </h2>
            <p className="text-slate-500 text-sm">Authorised personnel only.</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg mb-5">
              <i className="ri-error-warning-line flex-shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Admin Email</label>
              <input
                type="email" required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="hi@gcsecomputing.co.uk"
                className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'} required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors pr-10"
                  disabled={isSubmitting}
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 cursor-pointer">
                  <i className={showPass ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? 'Signing in...' : 'Sign In to Admin Panel'}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between">
            <Link to="/tutor/login" className="text-sm text-emerald-700 hover:text-emerald-800 font-medium cursor-pointer">
              Tutor login instead &rarr;
            </Link>
            <Link to="/" className="text-sm text-slate-400 hover:text-slate-600 cursor-pointer">
              Back to site
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
